import type { CSSProperties } from 'react';

import useStates from './useStates';

export type OpenParams<T> = {
  modalState?: T;
  pos?: CSSProperties;
};

export type ModalStateInterface<T = undefined> = {
  status: boolean;
  pos: CSSProperties;
  modalState: T;
  setStatus: (status: boolean) => void;
  setPos: (pos: CSSProperties) => void;
  setModalState: (modalState: T) => void;
  open: (params?: OpenParams<T>) => void;
  close: () => Promise<void>;
  toggle: () => void;
};

const useModalState = <T = undefined>(props?: T): ModalStateInterface<T> => {
  const { state, setState, setValue } = useStates<ModalToggleStatusT<T>>({
    status: false,
    pos: {},
    modalState: props,
  });

  const open = (params?: OpenParams<T>) => {
    if (params) {
      setState((prev) => ({
        ...prev,
        status: true,
        modalState: params?.modalState || prev.modalState,
        pos: params?.pos || prev.pos,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        status: true,
      }));
    }
  };

  const close = async () => {
    setValue('status', false);
    await new Promise((resolve) => setTimeout(resolve, 200));
  };
  const setStatus = (status: boolean) => setValue('status', status);
  const setPos = (pos: CSSProperties) => setValue('pos', pos);
  const setModalState = (modalState: T) => setValue('modalState', modalState);

  return {
    status: state.status,
    pos: state.pos as CSSProperties,
    modalState: state.modalState as T,
    setStatus,
    setPos,
    setModalState,
    open,
    close,
    toggle: () => {
      if (state.status) {
        open();
      } else {
        close();
      }
    },
  };
};

export default useModalState;
